import { useGlobalModal } from '@/hooks/useGlobalModal';
import GlobalModal from '@components/GlobalModal';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import TruncatedTypography from '@components/TruncatedTypography';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box } from '@mui/material';
import * as ExcelJS from 'exceljs';
import { type FC } from 'react';
import { CellComponentProps, Grid } from 'react-window';
import { DownloadDataInput } from '../types';

export const DownloadDataModal: FC = () => {
  const { globalModalProps, onClose, state } = useGlobalModal('downloadData');

  return (
    <GlobalModal {...globalModalProps}>
      <ModalTitle title={<Trans>Descargar datos</Trans>} onClose={onClose} />
      {state.open && (
        <DataTable data={{ rows: state.rows, headers: state.headers }} onClose={onClose} />
      )}
    </GlobalModal>
  );
};

const parseValue = (value: string | number | Date | null) => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    const formatter = new Intl.NumberFormat('es-CL', {
      useGrouping: false,
      maximumFractionDigits: 20,
    });
    return formatter.format(value);
  }
  if (value instanceof Date) {
    // to format as YYYY-MM-DD HH:MM
    const formatter = new Intl.DateTimeFormat('sv', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: 'numeric',
    });
    return formatter.format(value);
  }
  if (value === null) return '';
};

const CellComponent: FC<
  CellComponentProps<{ headers: string[]; rows: (string | number | Date | null)[][] }>
> = ({ headers, rows, style, rowIndex, columnIndex, ariaAttributes }) => {
  const isHeader = rowIndex === 0;
  const value = rowIndex === 0 ? headers[columnIndex] : rows[rowIndex - 1][columnIndex];

  return (
    <Box
      style={style}
      {...ariaAttributes}
      sx={{
        borderLeft: columnIndex === 0 ? 1 : 0,
        borderTop: rowIndex === 0 ? 1 : 0,
        borderBottom: 1,
        borderRight: 1,
        py: '1px',
        px: '4px',
        borderColor: 'divider',
      }}
    >
      <TruncatedTypography fontSize="small" fontWeight={isHeader ? 'bold' : undefined}>
        {parseValue(value)}
      </TruncatedTypography>
    </Box>
  );
};

const DataTable: FC<{ data: DownloadDataInput; onClose: () => void }> = ({ data, onClose }) => {
  const { i18n } = useLingui();

  const onDownloadDataClick = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Datos');

    const headerRow = worksheet.addRow(data.headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };
      cell.border = {
        bottom: { style: 'thin' },
      };
    });

    data.rows.forEach((rowData) => {
      const data = rowData.map((value) => {
        if (value instanceof Date) {
          return new Date(
            Date.UTC(
              value.getFullYear(),
              value.getMonth(),
              value.getDate(),
              value.getHours(),
              value.getMinutes(),
              value.getSeconds(),
            ),
          );
        }
        return value;
      });
      worksheet.addRow(data);
    });

    worksheet.columns[0].width = 12;

    worksheet.columns.forEach((column, index) => {
      if (index === 0) {
        // date column width
        column.width = 22;
      } else {
        // data columns width
        column.width = 22;
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', 'devices-analysis-data.xlsx');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <ModalContent>
        <Box width="100%" height={400}>
          <Grid
            cellComponent={CellComponent}
            cellProps={data}
            columnCount={data.headers.length}
            columnWidth={200}
            rowCount={data.rows.length + 1}
            rowHeight={25}
          />
        </Box>
      </ModalContent>
      <ModalActions
        onClose={onClose}
        customButtons={[
          {
            label: t(i18n)`Descargar`,
            onClick: onDownloadDataClick,
          },
        ]}
      />
    </>
  );
};
